import { debounce } from 'lodash';
import { useMemo, useState, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { PICKER_PAGE, pickerSortConfig, pickerHelperText, pickerNoOptionsText, buildPickerColumnFilters } from 'src/utils/picker-search';

import { useTranslate } from 'src/locales';
import { CallListPersonType } from 'src/services/call-lists/call-lists.types';
import { useGetMyCandidatesLeanQuery } from 'src/services/candidates/candidates.service';
import { useGetOrganizationContactsLeanQuery } from 'src/services/contacts/contacts.service';

// ----------------------------------------------------------------------

export interface PickedPerson {
  id: string;
  name: string;
  detail?: string;
}

type Props = {
  type: CallListPersonType;
  onTypeChange: (type: CallListPersonType) => void;
  value: PickedPerson[];
  onChange: (value: PickedPerson[]) => void;
};

/**
 * Search contacts or candidates by name or email and pick several, for Add people on the call list
 * page. Uses the same lean list queries as the Add to list picker (JT1-1586).
 */
export default function CallListPeoplePicker({ type, onTypeChange, value, onChange }: Props) {
  const { t } = useTranslate();
  const authContext = useAuthContext();
  const [searchQuery, setSearchQuery] = useState('');

  const onSearch = useMemo(() => debounce((text: string) => setSearchQuery(text), 400), []);
  useEffect(() => () => onSearch.cancel(), [onSearch]);

  const page = PICKER_PAGE;
  const sortConfig = pickerSortConfig(searchQuery);
  const columnFilters = buildPickerColumnFilters(searchQuery, { email: true });

  const contactsQuery = useGetOrganizationContactsLeanQuery({
    org_id: authContext.organization.id,
    page,
    sort_config: sortConfig,
    column_filters: columnFilters,
  }, { skip: type !== CallListPersonType.CONTACT });

  const candidatesQuery = useGetMyCandidatesLeanQuery({
    page,
    sortConfig,
    column_filters: columnFilters,
  }, { skip: type !== CallListPersonType.CANDIDATE });

  const activeQuery = type === CallListPersonType.CONTACT ? contactsQuery : candidatesQuery;

  const options: PickedPerson[] = useMemo(() => {
    if (type === CallListPersonType.CONTACT) {
      return (contactsQuery.currentData?.results ?? []).map((contact) => ({
        id: contact.id,
        name: [contact.first_name, contact.last_name].filter(Boolean).join(' '),
        detail: contact.email ?? undefined,
      }));
    }
    return (candidatesQuery.currentData?.results ?? []).map((candidate) => ({
      id: candidate.id,
      name: [candidate.first_name, candidate.last_name].filter(Boolean).join(' '),
      detail: candidate.email ?? undefined,
    }));
  }, [type, contactsQuery.currentData, candidatesQuery.currentData]);

  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={type}
        onChange={(_event, next: CallListPersonType | null) => {
          if (!next || next === type) return;
          onChange([]);
          onTypeChange(next);
        }}
      >
        <ToggleButton value={CallListPersonType.CONTACT}>{t('call_lists.add.contacts')}</ToggleButton>
        <ToggleButton value={CallListPersonType.CANDIDATE}>{t('call_lists.add.candidates')}</ToggleButton>
      </ToggleButtonGroup>

      <Autocomplete
        multiple
        options={options}
        value={value}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        loading={activeQuery.isFetching}
        noOptionsText={pickerNoOptionsText(t, !!activeQuery.isError)}
        filterOptions={(list) => list.filter((option) => option.name)}
        onChange={(_event, next) => onChange(next)}
        onInputChange={(_event, text, reason) => reason !== 'reset' && onSearch(text)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t(type === CallListPersonType.CONTACT ? 'call_lists.add.search_contacts' : 'call_lists.add.search_candidates')}
            helperText={pickerHelperText(t, activeQuery.currentData)}
          />
        )}
        renderOption={({ key, ...rest }, option) => (
          <li key={key} {...rest}>
            <Stack>
              <Typography variant="body2">{option.name}</Typography>
              {option.detail && <Typography variant="caption" color="text.secondary">{option.detail}</Typography>}
            </Stack>
          </li>
        )}
      />
    </Stack>
  );
}
